import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import ContactSupportRoundedIcon from "@mui/icons-material/ContactSupportRounded";
import DoorSlidingRoundedIcon from "@mui/icons-material/DoorSlidingRounded";
import FaceRoundedIcon from "@mui/icons-material/FaceRounded";
import GeneratingTokensRoundedIcon from "@mui/icons-material/GeneratingTokensRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import RoomPreferencesRoundedIcon from "@mui/icons-material/RoomPreferencesRounded";
import {
  Avatar,
  Chip,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  chipClasses,
} from "@mui/material";

import { usePlayer } from "@/components/player";
import { desaturatedOrange } from "@/config";
import { useSnackbar } from "@/hooks";
import { IconButtonMenu } from "@/primitives";
import { useCustomer } from "@/providers";
import { AuthQueries } from "@/queries";
import { AppRoutes } from "@/routes";

export const AccountMenu = () => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();

  const { t } = useTranslation();
  const { stop } = usePlayer();

  const { mutateAsync: signOut, isPending: isSignOutPending } =
    AuthQueries.useHandleSignOut();

  const { customer } = useCustomer();

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      stop();
      navigate(AppRoutes.ROOT());
    } catch (err) {
      console.error(err);
      snackbar.error(t("errors.signOut"));
    }
  }, [signOut, stop, navigate, snackbar, t]);

  // Display credits remaining
  const credits = customer?.creditsRemaining ?? 0;

  return (
    <>
      <IconButtonMenu
        edge="end"
        size="small"
        menuProps={{
          anchorOrigin: { horizontal: "center", vertical: "bottom" },
          slotProps: { list: { sx: { minWidth: "260px" } } },
          transformOrigin: { horizontal: "center", vertical: "top" },
        }}
        menu={
          <>
            <ListItem
              sx={{
                bgcolor: desaturatedOrange[50],
                borderRadius: 0.5,
                boxShadow: `inset 0 0 0.75px ${desaturatedOrange[300]}`,
              }}
            >
              <ListItemIcon sx={({ spacing }) => ({ minWidth: spacing(4) })}>
                <GeneratingTokensRoundedIcon
                  fontSize="small"
                  sx={{ color: "text.disabled" }}
                />
              </ListItemIcon>
              <ListItemText
                slotProps={{
                  primary: {
                    variant: "subtitle2",
                    sx: { mb: 0.25 },
                    color: "primary.dark",
                  },
                  secondary: { variant: "caption", color: "text.secondary" },
                }}
                primary={t("common.topbar.userMenu.creditsPrimary")}
                secondary={t("common.topbar.userMenu.creditsSecondary")}
              />
              <Chip
                color="primary"
                label={credits}
                sx={{
                  flexGrow: 2,
                  flexShrink: 0,
                  ml: 2,
                  [`& .${chipClasses.label}`]: {
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  },
                }}
                variant="outlined"
              />
            </ListItem>
            <MenuItem
              sx={{ mt: 1 }}
              onClick={() => {
                navigate(AppRoutes.SETTINGS());
              }}
            >
              <ListItemIcon>
                <RoomPreferencesRoundedIcon
                  fontSize="small"
                  sx={{ color: "text.disabled" }}
                />
              </ListItemIcon>
              <ListItemText
                slotProps={{
                  primary: {
                    variant: "subtitle2",
                    sx: { mb: 0.25 },
                    color: "primary.dark",
                  },
                  secondary: { variant: "caption", color: "text.secondary" },
                }}
                primary={t("common.topbar.userMenu.settingsPrimary")}
                secondary={t("common.topbar.userMenu.settingsSecondary")}
              />
            </MenuItem>
            <Divider />
            <MenuItem component={RouterLink} to={AppRoutes.ABOUT()}>
              <ListItemIcon>
                <InfoRoundedIcon
                  fontSize="small"
                  sx={{ color: "text.disabled" }}
                />
              </ListItemIcon>
              <ListItemText
                slotProps={{
                  primary: {
                    variant: "subtitle2",
                    sx: { mb: 0.25 },
                    color: "primary.dark",
                  },
                  secondary: { variant: "caption", color: "text.secondary" },
                }}
                primary={t("common.topbar.userMenu.aboutPrimary")}
                secondary={t("common.topbar.userMenu.aboutSecondary")}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                window.location.href = "mailto:hello@baiki.app";
              }}
            >
              <ListItemIcon>
                <ContactSupportRoundedIcon
                  fontSize="small"
                  sx={{ color: "text.disabled" }}
                />
              </ListItemIcon>
              <ListItemText
                slotProps={{
                  primary: {
                    variant: "subtitle2",
                    sx: { mb: 0.25 },
                    color: "primary.dark",
                  },
                  secondary: { variant: "caption", color: "text.secondary" },
                }}
                primary={t("common.topbar.userMenu.supportPrimary")}
                secondary={t("common.topbar.userMenu.supportSecondary")}
              />
            </MenuItem>
            <Divider />
            <MenuItem disabled={isSignOutPending} onClick={handleSignOut}>
              <ListItemIcon>
                <DoorSlidingRoundedIcon
                  fontSize="small"
                  sx={{ color: "text.disabled" }}
                />
              </ListItemIcon>
              <ListItemText
                slotProps={{
                  primary: {
                    variant: "subtitle2",
                    sx: { mb: 0.25 },
                    color: "primary.dark",
                  },
                  secondary: { variant: "caption", color: "text.secondary" },
                }}
                primary={t("common.topbar.userMenu.signOutPrimary")}
                secondary={t("common.topbar.userMenu.signOutSecondary")}
              />
            </MenuItem>
          </>
        }
      >
        <Avatar
          sx={{
            bgcolor: "primary.main",
            height: 32,
            width: 32,
          }}
        >
          <FaceRoundedIcon fontSize="small" />
        </Avatar>
      </IconButtonMenu>
    </>
  );
};
